import { Box, Divider, Paper, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { IPagingData, PurchaseDto } from '@sr/dto';
import { GridLayout, HeadCellsCollection } from 'shared/ui/grid-layout';
import { LinkToMedia } from 'shared/ui/links/link-to-media';
import { LinkToPurchase } from 'shared/ui/links/link-to-purchase';
import PriceSpan from 'shared/ui/price-span';
import { formatShortDatesAndPeriod } from 'utils/date-format-helpers';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import { LinkToInvoice } from 'entities/client-invoices-list/ui/link-to-invoice';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { useAvgMediaType } from '../media-types/media-type.hooks';

const columns: HeadCellsCollection = ['Заявка', 'Поставщик', 'СМИ', 'Счёт', 'Подробности', 'Количество', 'Период', 'Стоимость', 'Прибыль', 'Средняя стоимость'];

type Props = {
  collection: IRemotePagedCollection<PurchaseDto>,
  paging: IPagingData,
  onChangePage: (paging: IPagingData) => void
}

export const MediaPurchasesList = ({ collection, paging, onChangePage }: Props) => {
  return (
    <>
      <GridLayout<PurchaseDto>
        columns={columns}
        items={collection.collection.items}
        pagingData={paging}
        onChangePaging={onChangePage}
        totalItemsCount={collection.collection.totalItemsCount}
        isLoading={collection.isLoading}
        error={collection.error}
        itemRender={purchase => (<PurchaseRow key={purchase.id} purchase={purchase} />)}
        noItemsText='Нет заявок'
        footer={(items) => <PurchaseListFooter rows={items} />}
      />
    </>
  );
};

const PurchaseRow = ({ purchase }: { purchase: PurchaseDto; }) => {
  const getLocationText = () => {
    if (purchase.clientInvoiceRow.media?.country && !purchase.clientInvoiceRow.media?.region && !purchase.clientInvoiceRow.media?.city) {
      return `${purchase.clientInvoiceRow.media?.country.name || purchase.clientInvoiceRow.media?.country}`;
    }
    if (purchase.clientInvoiceRow.media?.region && !purchase.clientInvoiceRow.media?.city) {
      return `${purchase.clientInvoiceRow.media?.region?.name || purchase.clientInvoiceRow.media?.region}`;
    }
    if (purchase.clientInvoiceRow.media?.city) {
      return `г. ${purchase.clientInvoiceRow.media?.city?.name || purchase.clientInvoiceRow.media?.city}`;
    }
    return '';
  };
  
  const mediaName = purchase.clientInvoiceRow.media &&
    `${purchase.clientInvoiceRow.media.name}, ${getLocationText()}`;
  
  const mediaTypeId = purchase.clientInvoiceRow.media?.mediaType.id;
  const cityId = purchase.clientInvoiceRow.media?.city?.id;
  const regionId = purchase.clientInvoiceRow.media?.region?.id;
  const countryId = purchase.clientInvoiceRow.media?.country?.id;

  const { item: avgPrices } = useAvgMediaType();

  const mediaTypeAvgData = avgPrices?.find((avg: { typeId: number }) => avg.typeId === mediaTypeId);
 
  const cityAvgData = mediaTypeAvgData?.cityAvgs?.find((city: { cityId: number }) => city.cityId === cityId);
  const cityAvgPrice = cityAvgData?.avgPrice;

  const regionAvgData = mediaTypeAvgData?.regionAvgs?.find((region: { regionId: number }) => region.regionId === regionId);
  const regionAvgPrice = regionAvgData?.avgPrice;

  const countryAvgData = mediaTypeAvgData?.countryAvgs?.find((country: { countryId: number }) => country.countryId === countryId);
  const countryAvgPrice = countryAvgData?.avgPrice || mediaTypeAvgData?.countryAvg;

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          transition: 'background-color 0.3s ease'
        }
      }}
    >
      <TableCell><LinkToPurchase purchase={purchase} /></TableCell>
      
      <TableCell>
        {purchase.supplierInvoice 
          ? <LinkToCompany company={purchase.supplierInvoice.sellerCompany} /> 
          : '—'}
      </TableCell>
      
      <TableCell>
        {purchase.clientInvoiceRow?.media 
          ? <LinkToMedia media={purchase.clientInvoiceRow?.media}>{mediaName}</LinkToMedia> 
          : '—'}
      </TableCell>
      
      <TableCell sx={{ minWidth: 150 }}>
        <Paper sx={{ p: 1, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 92, textAlign: 'center' }}>
          <Box sx={{ flex: 1 }}>Исх.: <LinkToInvoice invoice={purchase.clientInvoice} /></Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}>{purchase.supplierInvoice ? <>Вх.: <LinkToInvoice invoice={purchase.supplierInvoice} /></> : '—'}</Box>
        </Paper>
      </TableCell>
      
      <TableCell>
        <Paper sx={{ p: 1, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 92 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Tooltip title={purchase.clientInvoiceRow.description} arrow>
              <Typography noWrap sx={{ maxWidth: 250 }}>
                {purchase.clientInvoiceRow.description.length > 100
                  ? `${purchase.clientInvoiceRow.description.substring(0, 97)}...`
                  : purchase.clientInvoiceRow.description}
              </Typography>
            </Tooltip>
            {purchase.clientInvoiceRow.media && (
              <span style={{ whiteSpace: 'nowrap' }}>
                {' '}(<LinkToMedia media={purchase.clientInvoiceRow.media} />)
              </span>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}>
            {purchase.supplierInvoiceRow ? (
              <Tooltip title={purchase.supplierInvoiceRow.description} arrow>
                <Typography noWrap sx={{ maxWidth: 300 }}>
                  {purchase.supplierInvoiceRow.description.length > 100
                    ? `${purchase.supplierInvoiceRow.description.substring(0, 97)}...`
                    : purchase.supplierInvoiceRow.description}
                </Typography>
              </Tooltip>
            ) : (
              '—'
            )}
          </Box>
        </Paper>
      </TableCell>
      
      <TableCell>
        <Paper sx={{ p: 1, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 92, textAlign: 'center' }}>
          <Box sx={{ flex: 1 }}>{purchase.clientInvoiceRow?.amount ? `${purchase.clientInvoiceRow.amount} шт` : '—'}</Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}>{purchase.supplierInvoiceRow?.amount ? `${purchase.supplierInvoiceRow.amount} шт` : '—'}</Box>
        </Paper>
      </TableCell>
      
      <TableCell>
        <Paper sx={{ p: 1, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 92, textAlign: 'center' }}>
          <Box sx={{ flex: 1 }}>{formatShortDatesAndPeriod(purchase.clientInvoiceRow.startDate, purchase.clientInvoiceRow.endDate)}</Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}>{purchase.supplierInvoiceRow ? formatShortDatesAndPeriod(purchase.supplierInvoiceRow.startDate, purchase.supplierInvoiceRow.endDate) : '—'}</Box>
        </Paper>
      </TableCell>
      
      <TableCell align='right'>
        <Paper sx={{ p: 1, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 92, textAlign: 'center' }}>
          <Box sx={{ flex: 1 }}><PriceSpan size='small' price={purchase.clientInvoiceRow.priceDiscount} /></Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}>{purchase.supplierInvoiceRow ? <PriceSpan size='small' price={purchase.supplierInvoiceRow.priceDiscount} /> : '—'}</Box>
        </Paper>
      </TableCell>
      
      <TableCell align='center'>
        <PriceSpan size='small' price={purchase.clientInvoiceRow.priceDiscount - (purchase.supplierInvoiceRow ? purchase.supplierInvoiceRow.priceDiscount : 0)} />
      </TableCell>
      
      <TableCell>
        <Paper sx={{ p: 1, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 92, textAlign: 'center' }}>
          <Box sx={{ flex: 1 }}><b>Город:</b>&nbsp;{cityAvgPrice ? <PriceSpan size="small" price={cityAvgPrice} /> : '—'}</Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}><b>Регион:</b>&nbsp;{regionAvgPrice ? <PriceSpan size="small" price={regionAvgPrice} /> : '—'}</Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ flex: 1 }}><b>Страна:</b>&nbsp;{countryAvgPrice ? <PriceSpan size="small" price={countryAvgPrice} /> : '—'}</Box>
        </Paper>
      </TableCell>
    </TableRow>
  );
};

type PurchaseListFooterProps = {
  rows: PurchaseDto[];
};
const PurchaseListFooter = ({ rows }: PurchaseListFooterProps) => {
  const totalProfit = rows.reduce((acc, row) => {
    const clientPrice = row.clientInvoiceRow.priceDiscount;
    const supplierPrice = row.supplierInvoiceRow?.priceDiscount || 0;
    return acc + (clientPrice - supplierPrice);
  }, 0);
  return (
    <TableRow>
      <TableCell colSpan={8} align="right">
        <Typography variant="subtitle1">Итого:</Typography>
      </TableCell>
      <TableCell>
        <PriceSpan price={totalProfit} size="small" />
      </TableCell>
    </TableRow>
  );
};
